import React from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../FaIcon';

function formatDate(isoDate, language) {
    if (!isoDate) return null;
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString(language || undefined);
}

/**
 * Chip discreto de estado de versión de un prompt copiado: "v9 disponible" (ámbar) si la
 * última copia de la agencia quedó vieja, "Al día" (gris sutil) si tiene la última. El
 * detalle ("copiaste la v6 el 3/8/2026") va en el tooltip.
 *
 * `status` = la entrada de copy-status del prompt ACTIVO ({current_version,
 * last_copied_version, last_copied_at}). Sin status o sin copia registrada no renderiza
 * nada: el chip solo APARECE — nunca transiciona de un estado visible a otro durante la
 * carga, así que no hay flicker.
 */
export function PromptVersionBadge({status}) {
    const {t, i18n} = useTranslation();
    if (!status || !status.last_copied_version || !status.current_version) return null;

    const upToDate = status.last_copied_version >= status.current_version;
    const date = formatDate(status.last_copied_at, i18n.language);
    const label = upToDate
        ? t('prompt_version.up_to_date')
        : t('prompt_version.update_available', {latest: status.current_version});
    const tooltip = date && t(
        upToDate ? 'prompt_version.current_tooltip' : 'prompt_version.stale_tooltip',
        {
            copied: status.last_copied_version,
            latest: status.current_version,
            date,
            // La fecha trae barras: el default de i18next las volvería entidades HTML.
            interpolation: {escapeValue: false},
        },
    );

    return (
        <span className={`prompt-version-badge info-icon-container ${upToDate ? '-current' : '-stale'}`}>
            <FaIcon iconName={upToDate ? 'check' : 'rotate'}/>
            <span className="prompt-version-badge-label">{label}</span>
            {tooltip && (
                <div className="warning-tooltip -below">
                    <div className="warning-tooltip-body">
                        <p className="warning-tooltip-text">{tooltip}</p>
                    </div>
                </div>
            )}
        </span>
    );
}
